import { Divider, LinearProgress, List, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import type { ProjectStatistics_ActiveProtocolItemFragment } from "../fragments.generated";
import { ClickThroughLink } from "./ClickThroughLink";
import { ProjectStatisticUnavailableCard } from "./ProjectStatisticUnavailableCard";

interface ProjectActiveProtocolOverviewCardProps {
    protocols: ProjectStatistics_ActiveProtocolItemFragment[];
}

export function ProjectActiveProtocolOverviewCard({ protocols }: ProjectActiveProtocolOverviewCardProps) {
    const { t } = useTranslation("project");

    if (protocols.length < 1) {
        return <ProjectStatisticUnavailableCard title={t("overviewScreen.activeProtocolOverview.title")} />;
    }

    return (
        <Paper sx={{ display: "flex", flexDirection: "column", padding: 2, rowGap: 1 }} variant="outlined">
            <Typography variant="h6" gutterBottom>
                {t("overviewScreen.activeProtocolOverview.title")}
            </Typography>
            <List>
                {protocols.map((protocol, index) => (
                    <React.Fragment key={protocol.protocolId}>
                        <Stack component="li" rowGap={1 / 2} paddingY={2}>
                            <Stack alignItems="center" direction="row" justifyContent="space-between">
                                <Typography variant="h6">{protocol.protocol.name}</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {protocol.completedVisitsPercentage.toFixed(0)}%
                                </Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={protocol.completedVisitsPercentage} />
                            <Typography variant="body2">
                                {t("overviewScreen.activeProtocolOverview.stats.numberOfAreas", {
                                    count: protocol.totalNumberOfAreas,
                                })}
                                {" | "}
                                {t("overviewScreen.activeProtocolOverview.stats.numberOfPlannedVisits", {
                                    count: protocol.totalNumberOfVisits,
                                })}
                                {" | "}
                                {t("overviewScreen.activeProtocolOverview.stats.numberOfCompletedVisits", {
                                    count: protocol.completedNumberOfVisits,
                                })}
                            </Typography>
                        </Stack>
                        {protocols.length - 1 > index && <Divider />}
                    </React.Fragment>
                ))}
            </List>

            <ClickThroughLink href="protocols" title={t("overviewScreen.activeProtocolOverview.link")} />
        </Paper>
    );
}
